import {
  type InfiniteData,
  keepPreviousData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
  type UseQueryDefinedReturnType,
} from "@tanstack/vue-query";
import { computed, type MaybeRef, unref } from "vue";

import URLS from "@/autogen/urls";
import { OkapiError, serializeQueryParams } from "@/lib/openapi";
import type { Aggregate } from "@/types/aggregate";
import type { GenericModel } from "@/types/model";
import { uniqueBy } from "@/lib/collections";

import { useNewsletterId, useXsrfHeaders } from "./utils";

// Flexible filter parameters type - values can be string, number, string array, or undefined
export type FilterParameters = Record<
  string,
  string | number | string[] | undefined
>;

// Check if filter parameters are empty (no active filters)
export const isFilterParametersEmpty = (
  parameters: FilterParameters | undefined
): boolean => {
  if (!parameters) return true;

  return Object.values(parameters).every((value) => {
    if (value === undefined || value === "") return true;
    if (Array.isArray(value)) return value.length === 0;
    return false; // number or non-empty string means filter is active
  });
};

declare const HYDRATED_PAYLOAD: Record<string, any>;

const getHydratedPayloadForListing = (
  path: string | undefined
):
  | { data: InfiniteData<ListingPage<any>, number>; fetchedAt: number }
  | undefined => {
  if (!path || !(path in HYDRATED_PAYLOAD)) {
    return;
  }

  const data = HYDRATED_PAYLOAD[path] as any[];
  if (data.length === 0) {
    return;
  }

  return {
    data: {
      // !! should match the `initialPageParam` in `useGenericListingQuery`
      pageParams: [1],
      pages: [
        {
          results: data,
          count: data.length,
          // TODO: `HYDRATED_PAYLOAD` currently doesn't tell us if there's
          // anything more we need to fetch
          next: undefined,
          previous: undefined,
        },
      ],
    },
    // `performance.timeOrigin` represents the time when navigation had started,
    // which should be good in this case
    fetchedAt: performance.timeOrigin,
  };
};

export interface ListingPage<T extends GenericModel> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

export const useGenericListingQuery = <T extends GenericModel>(opts: {
  queryKey: QueryKey;
  enabled?: MaybeRef<boolean>;
  maintainData?: boolean;
  hydrateFrom?: string;
  fetch: (opts: {
    page: number;
    signal: AbortSignal;
  }) => Promise<ListingPage<T>>;
}) => {
  const initialData = getHydratedPayloadForListing(opts.hydrateFrom);

  const query = useInfiniteQuery({
    queryKey: opts.queryKey,
    enabled: () => unref(opts.enabled) ?? true,
    async queryFn({ pageParam, signal }): Promise<ListingPage<T>> {
      return await opts.fetch({ page: pageParam, signal });
    },

    // `as never` because we don't want to infer the query data from this
    // property, as this is typed with `any` (I couldn't find a way to make
    // getHydratedPayload return a typed version without TanStack Query
    // complaining about page params for some reason, this works.)
    initialData: initialData?.data as never,
    initialDataUpdatedAt: initialData?.fetchedAt,

    placeholderData: opts.maintainData ? keepPreviousData : undefined,

    initialPageParam: 1,
    getNextPageParam(lastPage, _allPages, lastPageParam) {
      return lastPage.next ? lastPageParam + 1 : undefined;
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof OkapiError) {
        // Never retry 422s because that implies a static error.
        if (error.response.status === 422) {
          return false;
        }
      }

      return failureCount < 3;
    },
  });

  const data = query.data;
  const flattenedData = computed(() => {
    const result = data.value;
    if (!result) {
      return [];
    }

    return uniqueBy(
      result.pages.flatMap((page) => page.results),
      (item) => item.id
    );
  });

  const count = computed(() => {
    // Use the last-fetched page
    return data.value?.pages?.at(-1)?.count;
  });

  return { ...query, flattenedData, count };
};

export const useGenericAggregateQuery = <
  P extends FilterParameters = FilterParameters,
  AK extends string = keyof P & string
>(opts: {
  queryKey: QueryKey;
  resource: string;
  parameters?: MaybeRef<P>;
}) => {
  const newsletterId = useNewsletterId();
  const headers = useXsrfHeaders();

  const query = useQuery({
    // not sure why `queryKey` doesn't like `opts.parameters`
    queryKey: [
      newsletterId,
      opts.resource,
      "aggregates",
      opts.parameters as any,
    ],
    async queryFn({ signal }) {
      const params = serializeQueryParams(unref(opts.parameters));

      const response = await fetch(URLS["aggregate"](opts.resource) + params, {
        signal,
        headers: headers.value,
      });
      const json = await response.json();

      return json as { [key in AK]?: Aggregate<string>[] };
    },
    initialData: {} as never,
    initialDataUpdatedAt: 0,
  });

  return query as UseQueryDefinedReturnType<
    { [key in AK]?: Aggregate<string>[] },
    Error
  >;
};
